import { useEffect } from "react";
import { Link } from "wouter";
import { Compass, Home, Calculator, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/seo-head";
import { buildBreadcrumbList } from "@/lib/seo-jsonld";

const NOT_FOUND_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Page Not Found (404) | RealtyBlocks",
    description: "The page you are looking for could not be found on RealtyBlocks.",
    url: "https://realtyblocks.co.in/404",
    inLanguage: "en-IN",
    isPartOf: { "@id": "https://realtyblocks.co.in/#website" },
  },
  buildBreadcrumbList([
    { name: "Home", path: "/" },
    { name: "Page Not Found", path: "/404" },
  ]),
];

function MissingMapIllustration() {
  return (
    <svg
      viewBox="0 0 320 220"
      className="w-full max-w-md mx-auto"
      role="img"
      aria-label="Compass on a stylised map illustration"
    >
      <defs>
        <linearGradient id="map-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary) / 0.10)" />
          <stop offset="100%" stopColor="hsl(var(--accent) / 0.12)" />
        </linearGradient>
        <linearGradient id="pin-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--accent))" />
          <stop offset="100%" stopColor="hsl(var(--primary))" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="320" height="220" rx="20" fill="url(#map-bg)" />
      <g stroke="hsl(var(--primary) / 0.18)" strokeWidth="1.5" fill="none">
        <path d="M0 60 Q90 40 160 70 T 320 60" />
        <path d="M0 110 Q90 90 160 120 T 320 110" />
        <path d="M0 160 Q90 140 160 170 T 320 160" />
      </g>
      <g opacity="0.7">
        <circle cx="60" cy="55" r="3" fill="hsl(var(--brand-teal))" />
        <circle cx="240" cy="105" r="3" fill="hsl(var(--brand-teal))" />
        <circle cx="100" cy="170" r="3" fill="hsl(var(--brand-teal))" />
      </g>
      <g transform="translate(160 110)">
        <circle r="44" fill="hsl(var(--background))" stroke="hsl(var(--border))" strokeWidth="1" />
        <circle r="38" fill="hsl(var(--primary) / 0.06)" />
        <text
          x="0"
          y="-22"
          textAnchor="middle"
          fontSize="9"
          fontWeight="700"
          fill="hsl(var(--muted-foreground))"
        >
          N
        </text>
        <path d="M0 -16 L7 0 L0 18 L-7 0 Z" fill="url(#pin-grad)" />
        <circle r="3" fill="hsl(var(--background))" />
      </g>
      <text
        x="160"
        y="200"
        textAnchor="middle"
        fontSize="11"
        fontWeight="600"
        fill="hsl(var(--muted-foreground))"
        letterSpacing="2"
      >
        OFF THE MAP
      </text>
    </svg>
  );
}

export default function NotFound() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "prerender-status-code";
    meta.content = "404";
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  return (
    <main
      id="main-content"
      className="min-h-screen w-full flex items-center justify-center bg-gradient-mesh px-4 py-12"
    >
      <SEOHead
        title="Page Not Found (404) | RealtyBlocks"
        description="The page you are looking for could not be found on RealtyBlocks."
        canonicalUrl="https://realtyblocks.co.in/404"
        noindex
        structuredData={NOT_FOUND_JSONLD}
      />
      <div className="max-w-2xl w-full text-center surface-elevated p-8 sm:p-12 animate-fade-in">
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold tracking-wider uppercase mb-6">
          <Compass className="h-3.5 w-3.5" />
          Error 404
        </div>
        <MissingMapIllustration />
        <h1 className="font-display text-display-xl text-foreground mt-6 mb-3">
          This address doesn't exist on our map
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-lg mx-auto mb-8">
          The page you're looking for has moved or was never built. Let's get you
          back to something useful — try the homepage or run a free valuation.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link href="/">
            <Button variant="default" size="lg" className="w-full sm:w-auto">
              <Home className="h-4 w-4" />
              Back to home
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
          <Link href="/valuation">
            <Button variant="accent" size="lg" className="w-full sm:w-auto">
              <Calculator className="h-4 w-4" />
              Free property valuation
            </Button>
          </Link>
        </div>
      </div>
    </main>
  );
}
